import React, { useEffect, useState } from 'react';
import { Settings, Loader2, Save, ShieldCheck, Info } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const AdminSettings = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, authCfg())
      .then(({ data }) => setForm(data))
      .finally(() => setLoading(false));
  }, []);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: Number(v) || 0 }));

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, {
        reward_percent: form.reward_percent,
        reward_weekly: form.reward_weekly,
        reward_monthly: form.reward_monthly,
        reward_gold: form.reward_gold,
        referral_bonus: form.referral_bonus,
        wallet_max_topup: form.wallet_max_topup,
        loyalty_cap_percent: form.loyalty_cap_percent,
        gold_discount_percent: form.gold_discount_percent,
      }, authCfg());
      setForm(data);
      setMsg({ ok: true, text: 'Settings saved. Applied instantly to new bookings/referrals.' });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Global Settings">
      {loading || !form ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          <p className="text-sm text-neutral-500 -mt-3 mb-6">Changes apply company-wide across the web AND mobile apps in real time.</p>

          <div className="grid md:grid-cols-2 gap-4">
            <Card title="Reward earning rates" subtitle="Auto-earned when a customer books">
              <NumberField label="One-off booking cashback"      unit="%"   value={form.reward_percent} onChange={(v) => change('reward_percent', v)} testid="settings-reward-percent" />
              <NumberField label="Weekly subscription bonus"      unit="pts" value={form.reward_weekly}  onChange={(v) => change('reward_weekly', v)}  testid="settings-reward-weekly" />
              <NumberField label="Monthly subscription bonus"     unit="pts" value={form.reward_monthly} onChange={(v) => change('reward_monthly', v)} testid="settings-reward-monthly" />
              <NumberField label="Gold membership bonus"          unit="pts" value={form.reward_gold}    onChange={(v) => change('reward_gold', v)}    testid="settings-reward-gold" />
            </Card>

            <Card title="Referral & wallet" subtitle="Program economics">
              <NumberField label="Referral bonus (each side, first booking)" unit="₹" value={form.referral_bonus}     onChange={(v) => change('referral_bonus', v)}     testid="settings-referral-bonus" />
              <NumberField label="Max single wallet top-up"                 unit="₹" value={form.wallet_max_topup}   onChange={(v) => change('wallet_max_topup', v)}   testid="settings-wallet-max-topup" />
              <NumberField label="Loyalty coins cap per order"             unit="%" value={form.loyalty_cap_percent} onChange={(v) => change('loyalty_cap_percent', v)} testid="settings-loyalty-cap" />
              <NumberField label="Gold membership discount"                unit="%" value={form.gold_discount_percent} onChange={(v) => change('gold_discount_percent', v)} testid="settings-gold-discount" />
            </Card>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button data-testid="settings-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save settings'}
            </button>
            {msg && (
              <span data-testid="settings-msg" className={`text-sm ${msg.ok ? 'text-[#2E7D32]' : 'text-red-600'}`}>{msg.text}</span>
            )}
          </div>

          {form.updated_at && (
            <p className="mt-4 text-[11px] text-neutral-500 flex items-center gap-1">
              <Info className="w-3 h-3" /> Last updated {new Date(form.updated_at).toLocaleString('en-IN')} {form.updated_by ? `by ${form.updated_by}` : ''}
            </p>
          )}
          <p className="mt-1 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> Stored in the shared settings collection.</p>
        </>
      )}
    </AdminLayout>
  );
};

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white border border-neutral-200 rounded-2xl p-5">
    <p className="text-sm font-bold">{title}</p>
    <p className="text-xs text-neutral-500 mb-4">{subtitle}</p>
    <div className="space-y-3">{children}</div>
  </div>
);

const NumberField = ({ label, unit, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs text-neutral-600 font-medium">{label}</span>
    <div className="mt-1 flex items-center gap-2">
      <input
        data-testid={testid}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-2 rounded-lg border border-neutral-200 text-sm font-bold focus:outline-none focus:border-[#5AA02C] focus:ring-2 focus:ring-[#5AA02C]/20"
      />
      <span className="text-xs font-bold text-neutral-500 min-w-[24px]">{unit}</span>
    </div>
  </label>
);

export default AdminSettings;
